/**
 * connect/intelligence.ts — pure internet-intelligence derivation.
 *
 * Everything derives from RECORDED speed reports and city facts. No data ⇒
 * honest nulls; stability needs at least 3 reports to say anything.
 */

import type { CityConnectivityFacts, InternetIntelligence, SpeedReport } from './models';

export function computeInternetIntelligence(
  city: string,
  reports: SpeedReport[],
  facts: CityConnectivityFacts | null,
): InternetIntelligence {
  const cityReports = reports.filter((r) => r.city === city);
  const verified = cityReports.filter((r) => r.verified);
  const avg = (rs: SpeedReport[]): number | null =>
    rs.length === 0 ? null : rs.reduce((s, r) => s + r.downloadMbps, 0) / rs.length;

  let stability: InternetIntelligence['stability'] = null;
  const averageDownloadMbps = avg(cityReports);
  if (cityReports.length >= 3 && averageDownloadMbps != null && averageDownloadMbps > 0) {
    const variance =
      cityReports.reduce((s, r) => s + (r.downloadMbps - averageDownloadMbps) ** 2, 0) / cityReports.length;
    const coefficientOfVariation = Math.sqrt(variance) / averageDownloadMbps;
    stability = coefficientOfVariation <= 0.35 ? 'stable' : 'variable';
  }

  return {
    city,
    reportCount: cityReports.length,
    averageDownloadMbps,
    verifiedAverageMbps: avg(verified),
    verifiedShare: cityReports.length === 0 ? null : verified.length / cityReports.length,
    stability,
    mobileCoverageNote: facts?.mobileCoverageNote ?? null,
    fiberAvailable: facts?.fiberAvailable ?? null,
    powerReliabilityNote: facts?.powerReliabilityNote ?? null,
    publicWifiNote: facts?.publicWifiNote ?? null,
  };
}
